/**
 * Subscription Plans / Pricing Page
 * Shows all available subscription tiers with updated pricing
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Crown, CheckCircle2, Building2, Wrench, Car, Loader2,
  ChevronLeft, Sparkles, Shield, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function PricingPage() {
  const { t } = useLanguage();

  // Plan type categories — labels/descriptions are resolved via i18n so that
  // selecting Norwegian Bokmål leaves no English leftovers on this page.
  const PLAN_CATEGORIES = {
    company: {
      label: t('pricing.categories.companyLabel'),
      description: t('pricing.categories.companyDesc'),
      icon: Building2,
    },
    rental: {
      label: t('pricing.categories.rentalLabel'),
      description: t('pricing.categories.rentalDesc'),
      icon: Wrench,
    },
    workshop: {
      label: t('pricing.categories.workshopLabel'),
      description: t('pricing.categories.workshopDesc'),
      icon: Car,
    },
  };
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/api/monetization/plans`);
      const data = await response.json();
      setPlans(data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error(t('pricing.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.info(t('auth.loginRequired'));
      navigate('/login');
      return;
    }

    setSubscribing(planId);
    try {
      await api.createMonetizationSubscription({ plan_id: planId });
      toast.success(t('subscription.activated'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubscribing(null);
    }
  };

  // Group plans by type
  const groupedPlans = {
    company: plans.filter(p => 
      (p.type === 'company' || p.id?.startsWith('company_')) && 
      !p.id?.includes('rental')
    ),
    rental: plans.filter(p => 
      p.type === 'rental_individual' || 
      p.type === 'rental_company' || 
      p.id?.startsWith('rental_')
    ),
    workshop: plans.filter(p => 
      p.type === 'auto_service' || 
      p.id?.startsWith('auto_service')
    )
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 bg-white/20 text-white">
            <Sparkles className="h-3 w-3 mr-1" />
            {t('pricing.trialBadge')}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {t('subscription.plans')}
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            {t('pricing.heroSubtitle')}
          </p>
        </div>
      </div>

      {/* Plan Categories */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="grid grid-cols-3 w-full max-w-lg">
              {Object.entries(PLAN_CATEGORIES).map(([key, category]) => (
                <TabsTrigger key={key} value={key} className="flex items-center gap-2">
                  <category.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{category.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Company Plans */}
          <TabsContent value="company">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">{PLAN_CATEGORIES.company.label}</h2>
              <p className="text-gray-600">{PLAN_CATEGORIES.company.description}</p>
            </div>
            <PlanGrid plans={groupedPlans.company} onSubscribe={handleSubscribe} subscribing={subscribing} />
          </TabsContent>

          {/* Rental Plans */}
          <TabsContent value="rental">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">{PLAN_CATEGORIES.rental.label}</h2>
              <p className="text-gray-600">{PLAN_CATEGORIES.rental.description}</p>
              <Badge variant="outline" className="mt-2">
                <Shield className="h-3 w-3 mr-1" />
                {t('pricing.subscriptionOnlyBadge')}
              </Badge>
            </div>
            <PlanGrid plans={groupedPlans.rental} onSubscribe={handleSubscribe} subscribing={subscribing} />
          </TabsContent>

          {/* Workshop Plans */}
          <TabsContent value="workshop">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">{PLAN_CATEGORIES.workshop.label}</h2>
              <p className="text-gray-600">{PLAN_CATEGORIES.workshop.description}</p>
            </div>
            <PlanGrid plans={groupedPlans.workshop} onSubscribe={handleSubscribe} subscribing={subscribing} />
          </TabsContent>
        </Tabs>

        {/* Job Seekers Section */}
        <div className="mt-16 bg-green-50 rounded-2xl p-8 text-center">
          <Users className="h-12 w-12 mx-auto text-green-600 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.jobSeekersTitle')}</h2>
          <p className="text-gray-600 mb-4">
            {t('pricing.jobSeekersIntro1')} <strong>{t('pricing.jobSeekersIntro2')}</strong>
          </p>
          <ul className="inline-flex flex-col items-start text-left gap-2 mb-6">
            <li className="flex items-center gap-2 text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('pricing.jobSeekersBenefit1')}
            </li>
            <li className="flex items-center gap-2 text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('pricing.jobSeekersBenefit2')}
            </li>
            <li className="flex items-center gap-2 text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('pricing.jobSeekersBenefit3')}
            </li>
            <li className="flex items-center gap-2 text-gray-700">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {t('pricing.jobSeekersBenefit4')}
            </li>
          </ul>
          <Button onClick={() => navigate('/register')} size="lg" data-testid="pricing-create-free-account-btn">
            {t('pricing.createFreeAccount')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Plan Grid Component
function PlanGrid({ plans, onSubscribe, subscribing }) {
  const { t } = useLanguage();

  // Map backend English feature/description strings to i18n keys so that
  // hardcoded English copy from /api/monetization/plans is localized
  // (no backend migration required).
  const featureKey = (s) => {
    if (!s || typeof s !== 'string') return '';
    return s
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };
  const translateFeature = (s) => {
    const k = featureKey(s);
    const val = t(`pricing.planFeatures.${k}`);
    return val && !val.startsWith('pricing.planFeatures.') && !val.startsWith('[MISSING') ? val : s;
  };
  const translateDesc = (s) => {
    const k = featureKey(s);
    // Reuse same map — fallback to raw string
    const val = t(`pricing.planDescriptions.${k}`);
    return val && !val.startsWith('pricing.planDescriptions.') && !val.startsWith('[MISSING') ? val : s;
  };
  
  if (!plans.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('pricing.noPlansAvailable')}
      </div>
    );
  }

  // Sort: trial first, then by price
  const sortedPlans = [...plans].sort((a, b) => {
    if (a.trial_days > 0 && b.trial_days === 0) return -1;
    if (a.trial_days === 0 && b.trial_days > 0) return 1;
    return a.price_monthly - b.price_monthly;
  });

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {sortedPlans.map((plan) => {
        const isTrial = plan.trial_days > 0;
        const isPopular = plan.tier === 'basic' && plan.price_monthly > 0;
        
        return (
          <Card 
            key={plan.id}
            className={`relative overflow-hidden transition-all hover:shadow-lg ${
              isPopular ? 'border-blue-500 ring-2 ring-blue-500/20' : ''
            }`}
          >
            {isPopular && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl">
                {t('pricing.mostPopular')}
              </div>
            )}
            
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Crown className={`h-5 w-5 ${isTrial ? 'text-amber-500' : 'text-blue-500'}`} />
                {plan.name}
              </CardTitle>
              <CardDescription>{translateDesc(plan.description)}</CardDescription>
            </CardHeader>
            
            <CardContent>
              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold">
                    {isTrial ? t('pricing.free') : `€${plan.price_monthly}`}
                  </span>
                  {!isTrial && (
                    <span className="text-gray-500">/{t('subscription.perMonth')}</span>
                  )}
                </div>
                {isTrial && (
                  <p className="text-sm text-amber-600 font-medium">
                    {t('pricing.dayFreeTrial', { days: plan.trial_days })}
                  </p>
                )}
                {plan.price_yearly > 0 && (
                  <p className="text-sm text-gray-500">
                    {t('pricing.yearlyNote', {
                      amount: plan.price_yearly,
                      per: t('subscription.perYear'),
                    })}
                  </p>
                )}
              </div>
              
              <ul className="space-y-2">
                {plan.features?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{translateFeature(feature)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            
            <CardFooter>
              <Button 
                className="w-full"
                variant={isTrial ? 'default' : 'outline'}
                onClick={() => onSubscribe(plan.id)}
                disabled={subscribing === plan.id}
              >
                {subscribing === plan.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isTrial ? (
                  t('pricing.startFreeTrial')
                ) : (
                  t('pricing.subscribe')
                )}
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}

export default PricingPage;
